import { useCallback, useEffect, useRef, useState } from 'react'
import { getDrivers, getReadyToSend, sendOut } from '../lib/api'
import type { DispatchDriver, ReadyToSendOrder } from '../types'
import { orderCode } from '../lib/order-code'
import { useSilentRefresh } from '../lib/useSilentRefresh'
import { CheckIcon } from '../components/Icons'
import S from '../locales/send-out.json'
import C from '../locales/collect.json'
import F from '../locales/final-check.json'
import { tr, useLang, type Lang } from '../lib/i18n'

type SendState = { kind: 'idle' } | { kind: 'pending'; driverId: string }

type Notice = { key: 'sendOutQueued' } | { key: 'sendOutSent'; codes: string; driver: string }

// ux3 (e): the driver button says what a tap does. The reason a button is disabled is its label.
export function driverButtonLabel(
  driver: DispatchDriver,
  selectedCount: number,
  pendingThis: boolean,
  lang: Lang,
): string {
  if (pendingThis) return tr(S.driverSending, lang)
  if (driver.busy) return tr(S.driverOnRun, lang, { driver: driver.name })
  if (selectedCount === 0) return tr(S.selectOrdersFirst, lang)
  return tr(S.sendWith, lang, { n: selectedCount, driver: driver.name })
}

function minutesReady(readyAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(readyAt).getTime()) / 60000))
}

// R4 — Nour's "Send out" screen: after her final check, she ticks the ready orders and
// taps a free driver. The run is created only on that tap (no automatic builder). In
// FinalCheckScreen's style and tokens. Bilal gets the same lists on HOP (DispatchScreen).
// R572 (b1): the screen's reads and its batch send are one hook, so the till's Orders face
// keeps the same ticks and the same "Send with <driver>" bar below its one list (ruling 2).
export function useSendOut() {
  const [orders, setOrders] = useState<ReadyToSendOrder[] | null>(null)
  const [drivers, setDrivers] = useState<DispatchDriver[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [sendState, setSendState] = useState<SendState>({ kind: 'idle' })
  const [statusNotice, setStatusNotice] = useState<Notice | null>(null)
  const [alertMessage, setAlertMessage] = useState<{ server: string } | 'failed' | null>(null)

  const inFlight = useRef(false)

  // ux3 (c): a silent load (the 20 s refresh) keeps the ticks on orders still listed and the
  // notices on screen, shows no "Loading" line, and on failure keeps the lists it has.
  const load = useCallback((silent: boolean = false) => {
    if (inFlight.current) return
    inFlight.current = true
    if (!silent) {
      setLoading(true)
      setLoadError(false)
    }
    Promise.all([getReadyToSend(), getDrivers()])
      .then(([readyOrders, readyDrivers]) => {
        setOrders(readyOrders)
        setDrivers(readyDrivers)
        if (silent) {
          const stillListed = new Set(readyOrders.map((o) => o.id))
          setSelected((prev) => new Set([...prev].filter((id) => stillListed.has(id))))
          return
        }
        setSelected(new Set())
        setStatusNotice(null)
        setAlertMessage(null)
      })
      .catch(() => {
        if (!silent) setLoadError(true)
      })
      .finally(() => {
        inFlight.current = false
        if (!silent) setLoading(false)
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useSilentRefresh(() => {
    if (sendState.kind === 'pending') return
    load(true)
  })

  function toggleOrder(orderId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  async function handleTapDriver(driver: DispatchDriver) {
    if (driver.busy || selected.size === 0 || sendState.kind === 'pending') return
    const orderIds = [...selected]
    const sentOrders = (orders ?? []).filter((o) => orderIds.includes(o.id))
    setAlertMessage(null)
    setSendState({ kind: 'pending', driverId: driver.id })
    try {
      const outcome = await sendOut(orderIds, driver.id)
      if (outcome.outcome === 'rejected') {
        setAlertMessage({ server: outcome.message })
        setSendState({ kind: 'idle' })
        return
      }
      setOrders((prev) => prev && prev.filter((o) => !orderIds.includes(o.id)))
      setSelected(new Set())
      const codes = sentOrders.map(orderCode).join(', ')
      setStatusNotice(
        outcome.outcome === 'queued'
          ? { key: 'sendOutQueued' }
          : { key: 'sendOutSent', codes, driver: driver.name },
      )
      setSendState({ kind: 'idle' })
    } catch {
      setAlertMessage('failed')
      setSendState({ kind: 'idle' })
    }
  }

  return { orders, drivers, loadError, loading, load, selected, toggleOrder, sendState, statusNotice, alertMessage, handleTapDriver }
}

export type SendOut = ReturnType<typeof useSendOut>

// R572 (b1): one ready order as a tick row, the card body of a ready-to-send order.
export function SendOutTick({ order, isSelected, onToggle, lang }: { order: ReadyToSendOrder; isSelected: boolean; onToggle: () => void; lang: Lang }) {
  return (
    <label
      className={
        isSelected
          ? 'flex min-h-16 items-center gap-3 rounded-xl border-2 border-navy bg-navy-tint px-4 py-2 cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-navy'
          : 'flex min-h-16 items-center gap-3 rounded-xl border-2 border-fleet-200 bg-white px-4 py-2 cursor-pointer hover:bg-navy-tint has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-navy'
      }
    >
      <input type="checkbox" checked={isSelected} onChange={onToggle} className="sr-only" />
      <span
        aria-hidden="true"
        data-tick
        className={
          isSelected
            ? 'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy text-white'
            : 'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border-2 border-fleet-300 bg-white text-transparent'
        }
      >
        <CheckIcon className="h-7 w-7" />
      </span>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <span className="font-semibold">{orderCode(order)}</span>
          <span className="text-sm text-fleet-500">{tr(S.readyMinutes, lang, { n: minutesReady(order.readyAt) })}</span>
        </div>
        <p className="text-sm text-fleet-600">
          {order.customerName ?? tr(C.unknownCustomer, lang)}
          {order.deliveryZoneName ? ` \u00b7 ${order.deliveryZoneName}` : ''}
        </p>
      </div>
    </label>
  )
}

// R572 (b1): the screen's notice and alert, shown above the Orders face's list as well.
export function SendOutNotices({ so, lang }: { so: SendOut; lang: Lang }) {
  return (
    <>
      {so.statusNotice && (
        <p
          id="send-out-status-notice"
          role="status"
          className="mb-4 rounded-lg bg-fleet-100 text-fleet-700 text-sm font-semibold px-4 py-3"
        >
          {so.statusNotice.key === 'sendOutSent'
            ? tr(S.sendOutSent, lang, { codes: so.statusNotice.codes, driver: so.statusNotice.driver })
            : tr(S.sendOutQueued, lang)}
        </p>
      )}

      {so.alertMessage && (
        <div
          id="send-out-alert"
          role="alert"
          className="mb-4 rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3"
        >
          {so.alertMessage === 'failed' ? tr(S.sendFailed, lang) : so.alertMessage.server}
        </div>
      )}
    </>
  )
}

// R572 (b1): the drivers, one "Send with <driver>" button each; on the Orders face this is the
// one bar below the list (ruling 2).
export function DriverBar({ so, lang }: { so: SendOut; lang: Lang }) {
  const { drivers, selected, sendState, handleTapDriver } = so
  if (drivers === null) return null
  return (
    <div>
      <h2 className="text-sm font-semibold text-fleet-500 uppercase tracking-wide mb-2">
        {tr(S.drivers, lang)}
      </h2>
      {drivers.length === 0 && (
        <p id="send-out-drivers-empty" className="text-sm text-fleet-500">
          {tr(S.driversEmpty, lang)}
        </p>
      )}
      {drivers.length > 0 && (
        <ul id="send-out-drivers-list" className="space-y-2">
          {drivers.map((driver) => {
            const isPendingThis = sendState.kind === 'pending' && sendState.driverId === driver.id
            const disabled = driver.busy || selected.size === 0 || sendState.kind === 'pending'
            return (
              <li key={driver.id}>
                <button
                  type="button"
                  data-driver-id={driver.id}
                  disabled={disabled}
                  onClick={() => void handleTapDriver(driver)}
                  className="w-full min-h-tap rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-fleet-300 text-white font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
                >
                  {driverButtonLabel(driver, selected.size, isPendingThis, lang)}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export function SendOutScreen() {
  const lang = useLang()
  const so = useSendOut()
  const { orders, loadError, loading, load, selected, toggleOrder } = so

  return (
    <div className="min-h-screen bg-fleet-50 px-6 py-8 font-body text-fleet-800">
      <div className="w-full max-w-sm mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-condensed text-2xl font-bold uppercase tracking-wide">{tr(S.sendOutTitle, lang)}</h1>
          <button
            id="send-out-refresh-btn"
            type="button"
            onClick={() => load()}
            className="min-h-tap text-sm font-semibold text-fleet-500 px-2"
          >
            {tr(C.refresh, lang)}
          </button>
        </div>

        <SendOutNotices so={so} lang={lang} />

        {loading && <p className="text-sm text-fleet-500">{tr(C.loading, lang)}</p>}

        {loadError && (
          <div className="space-y-3">
            <div
              id="send-out-load-error"
              role="alert"
              className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3"
            >
              {tr(S.sendOutLoadError, lang)}
            </div>
            <button
              id="send-out-try-again-btn"
              type="button"
              onClick={() => load()}
              className="w-full min-h-tap rounded-lg bg-fleet-700 text-white font-semibold px-4 py-2"
            >
              {tr(F.tryAgain, lang)}
            </button>
          </div>
        )}

        {!loading && !loadError && orders !== null && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-fleet-500 uppercase tracking-wide mb-2">
              {tr(S.readyToSend, lang)}
            </h2>
            {orders.length === 0 && (
              <p id="send-out-ready-empty" className="text-sm text-fleet-500">
                {tr(S.readyEmpty, lang)}
              </p>
            )}
            {orders.length > 0 && (
              <ul id="send-out-ready-list" className="space-y-2">
                {orders.map((order) => {
                  const isSelected = selected.has(order.id)
                  return (
                  <li key={order.id} data-order-id={order.id} data-selected={isSelected ? 'true' : 'false'}>
                    {/* ux3 (e): the whole row is the label, so a tap anywhere on it toggles the
                        order. The native box stays for keyboard and screen readers; the big tick
                        is what the eye reads. */}
                    <SendOutTick order={order} isSelected={isSelected} onToggle={() => toggleOrder(order.id)} lang={lang} />
                  </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        {!loading && !loadError && <DriverBar so={so} lang={lang} />}
      </div>
    </div>
  )
}
